import { useEffect, useRef, useState } from 'react'

import * as Sentry from '@sentry/nextjs'

import {
  useMarkFtuCompletedMutation,
  useMySettingLazyQuery,
} from '@/graphql/generated/hooks'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { mergeCompletedFtuIds, selectCompletedFtuIds } from '@/redux/ui/uiSlice'

type FtuCompletionSyncResult = {
  // True once the backend's completed set has been merged for this user, or the
  // read failed and local state is the fallback. The manager waits for it so a
  // tour finished on another device never flashes here before the answer lands.
  isSynced: boolean
}

/**
 * Once per login, pulls the user's completed FTU ids from the backend into Redux
 * and pushes up any ids that only local storage knows about (completions made
 * before backend persistence existed), so existing users are not re-shown tours
 * they already finished on this browser.
 */
export const useFtuCompletionSync = (
  userId?: string
): FtuCompletionSyncResult => {
  const dispatch = useAppDispatch()
  const localIds = useAppSelector(selectCompletedFtuIds)
  const localIdsRef = useRef(localIds)
  localIdsRef.current = localIds

  const [loadMySetting] = useMySettingLazyQuery({ fetchPolicy: 'network-only' })
  const [markFtuCompleted] = useMarkFtuCompletedMutation()
  const [syncedUserId, setSyncedUserId] = useState<string | undefined>()

  useEffect(() => {
    if (!userId) {
      return
    }

    let isCancelled = false

    loadMySetting()
      .then(({ data, error }) => {
        if (isCancelled) {
          return
        }

        const backendIds = data?.mySetting?.completedFtus
        if (!backendIds) {
          throw error ?? new Error('mySetting returned no completedFtus')
        }

        dispatch(mergeCompletedFtuIds(backendIds))

        localIdsRef.current
          .filter((id) => !backendIds.includes(id))
          .forEach((id) => {
            markFtuCompleted({ variables: { input: { ftuId: id } } }).catch(
              (mutationError) => {
                Sentry.captureException(mutationError, {
                  tags: { feature: 'first_time_use', action: 'migrate_local' },
                  extra: { ftuId: id },
                })
              }
            )
          })
      })
      .catch((error) => {
        // Fall back to local state; the tours still gate on Redux.
        Sentry.captureException(error, {
          tags: { feature: 'first_time_use', action: 'hydrate_completed' },
        })
      })
      .finally(() => {
        if (!isCancelled) {
          setSyncedUserId(userId)
        }
      })

    return () => {
      isCancelled = true
    }
  }, [dispatch, loadMySetting, markFtuCompleted, userId])

  return { isSynced: Boolean(userId) && syncedUserId === userId }
}
